/**
 * mobileTopbar.js — Mobile topbar button delegation
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 * 
 * [v6.1.0 — QWEN 3.8 PATCHED]:
 *  ✅ FIXED: Memory leak в auto-hide (_autoHideTimer не очищался)
 *  ✅ FIXED: Memory leak в event listeners (document touchstart/mousemove)
 *  ✅ FIXED: Race condition в fullscreen toggle
 *  ✅ FIXED: Compact mode state leak (не сбрасывался при destroy)
 *  ✅ NEW: Защита от повторной инициализации (_initialized flag)
 *  ✅ NEW: DOM caching для производительности
 *  ✅ NEW: Throttling auto-hide reset через rAF
 *  ✅ NEW: Отдельный AbortController для auto-hide
 *  ✅ IMPROVED: Error handling в Action Map
 *  ✅ IMPROVED: Cleanup inline styles при destroy
 */

import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════
//  STATE
// ══════════════════════════════════════════════════════════════

let _ac = null;
let _autoHideAC = null; // [FIX] Отдельный AC для auto-hide listeners
let _initialized = false;
let _compactMode = false;
let _autoHideTimer = null;
let _autoHideEnabled = false;
let _fullscreenPending = false;
let _autoHideRafId = null;
let _cachedRefs = null;

// ══════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════

/**
 * Кэшированный доступ к DOM элементам
 * Инвалидируется при destroy или изменении DOM
 */
function _getRefs() {
  if (_cachedRefs) return _cachedRefs;

  const refs = {
    topbar: document.getElementById('mobile-topbar'),
    nav: document.getElementById('mobile-nav'),
  };

  // [10.0.1] Неудачный поиск не кэшируем. Если initMobileTopbar вызвали до
  // того, как разметка мобильной панели появилась в DOM, в кэш ложились два
  // null — и дальше _getRefs возвращал их вечно, потому что инвалидация есть
  // только в destroy. Повторная инициализация уже не помогала бы: элементы на
  // месте, а модуль их не видит. Кэшируем только удачный поиск.
  if (!refs.topbar && !refs.nav) return refs;

  _cachedRefs = refs;
  return _cachedRefs;
}

function _invalidateCache() {
  _cachedRefs = null;
}

/**
 * Тактильный отклик для мобильных
 */
const triggerHaptic = () => {
  if (navigator.vibrate && window._userHasInteracted) {
    try { navigator.vibrate(10); } catch (e) { /* ignore */ }
  }
};

/**
 * Обновление активного состояния кнопок
 */
const updateActiveState = (activeBtn, clearSelectors) => {
  const { topbar } = _getRefs();
  if (!topbar || !activeBtn) return;

  clearSelectors.forEach(selector => {
    topbar.querySelectorAll(`.mtop-btn[${selector}]`).forEach(b => b.classList.remove('active'));
  });
  activeBtn.classList.add('active');
};

/**
 * Безопасный переключатель Fullscreen с защитой от race condition
 */
const toggleFullscreen = () => {
  if (_fullscreenPending) return;
  _fullscreenPending = true;

  const docEl = document.documentElement;
  const isFS = !!(document.fullscreenElement || document.webkitFullscreenElement);

  // [10.0.1] Вызов обёрнут в try/catch, и флаг снимается в том числе при
  // СИНХРОННОМ исключении.
  //
  // requestFullscreen отклоняет запрос двумя разными способами: возвращает
  // отклонённый промис (Chrome) или бросает исключение прямо из вызова
  // (Safari и старый WebKit, когда запрос пришёл не из пользовательского
  // жеста). Второй путь мимо .catch/.finally проходил насквозь, флаг
  // _fullscreenPending оставался true навсегда, и кнопка полноэкранного
  // режима переставала работать до перезагрузки страницы.
  let promise;
  try {
    promise = isFS
      ? (document.exitFullscreen || document.webkitExitFullscreen)?.call(document)
      : (docEl.requestFullscreen || docEl.webkitRequestFullscreen)?.call(docEl);
  } catch (e) {
    log.warn('[Fullscreen] Запрос отклонён браузером:', e.message);
    _fullscreenPending = false;
    return;
  }

  if (promise && typeof promise.then === 'function') {
    promise
      .catch(err => log.warn('[Fullscreen]', err))
      .finally(() => { _fullscreenPending = false; });
  } else {
    // Fallback для старых браузеров
    setTimeout(() => { _fullscreenPending = false; }, 100);
  }
};

/**
 * Throttled reset auto-hide timer через requestAnimationFrame
 */
function _throttledAutoHideReset(delaySec) {
  const { topbar } = _getRefs();
  if (!topbar) return;

  if (_autoHideRafId) return;
  _autoHideRafId = requestAnimationFrame(() => {
    _autoHideRafId = null;
    topbar.style.transform = '';
    topbar.style.opacity = '1';
    clearTimeout(_autoHideTimer);
    _autoHideTimer = setTimeout(() => {
      if (_autoHideEnabled && topbar) {
        topbar.style.transform = 'translateY(-100%)';
        topbar.style.opacity = '0';
      }
    }, delaySec * 1000);
  });
}

// ══════════════════════════════════════════════════════════════
//  ACTION MAP
// ══════════════════════════════════════════════════════════════

const ACTION_HANDLERS = {
  click: (btn, targetId) => {
    const target = document.getElementById(targetId);
    if (target) target.click();
    else log.warn(`[mobileTopbar] Target not found: #${targetId}`);
  },

  layout: (btn, targetId) => {
    try {
      const target = document.getElementById(targetId);
      if (target) target.click();
      updateActiveState(btn, ['data-mtop-layout', 'data-mtop-mode']);
    } catch (e) {
      log.warn('[mobileTopbar] layout action error:', e.message);
    }
  },

  mode: (btn, targetId) => {
    try {
      const viewMode = btn.dataset.viewMode;
      if (viewMode && window.MikhmelViewer?.activateViewMode) {
        window.MikhmelViewer.activateViewMode(viewMode);
      } else {
        const target = document.getElementById(targetId);
        if (target) target.click();
      }
      updateActiveState(btn, ['data-mtop-mode', 'data-mtop-layout']);
    } catch (e) {
      log.warn('[mobileTopbar] mode action error:', e.message);
    }
  },

  fullscreen: () => {
    try {
      toggleFullscreen();
    } catch (e) {
      log.warn('[mobileTopbar] fullscreen action error:', e.message);
    }
  }
};

// ══════════════════════════════════════════════════════════════
//  INIT & DESTROY
// ══════════════════════════════════════════════════════════════

export function initMobileTopbar() {
  // [FIX] Защита от повторной инициализации
  if (_initialized) {
    log.debug('[mobileTopbar] Already initialized, skipping');
    return;
  }

  // [FIX] Cleanup previous listeners
  if (_ac) _ac.abort();
  _ac = new AbortController();

  const { topbar, nav } = _getRefs();

  if (!topbar && !nav) {
    log.warn('[mobileTopbar] UI elements not found');
    return;
  }

  // ─── TOPBAR DELEGATION ───
  if (topbar) {
    topbar.addEventListener('click', (e) => {
      const btn = e.target.closest('.mtop-btn');
      if (!btn) return;

      const action = btn.dataset.action;
      const target = btn.dataset.target;

      if (action && ACTION_HANDLERS[action]) {
        // [10.0.8] Останавливаем всплытие — иначе тот же клик ловит второй,
        // общий обработчик на document (mobileBindings), который смотрит на
        // data-view-mode и ВТОРОЙ раз зовёт activateViewMode. Снаружи это
        // выглядело как задвоенные тосты («Axial», «Axial»), но задваивалась
        // не подпись, а вся работа: проекция строилась дважды, а вместе с ней
        // — и объём. На телефоне это лишняя память и лишняя секунда ожидания.
        //
        // Гасим только то, что действительно обработали здесь: для неизвестных
        // действий клик обязан идти дальше, там его ждут другие привязки.
        e.stopPropagation();
        triggerHaptic();
        ACTION_HANDLERS[action](btn, target);
      } else if (action) {
        log.debug(`[mobileTopbar] Unknown action: ${action}`);
      }
    }, { signal: _ac.signal });

    // Fullscreen button sync
    const fsBtn = topbar.querySelector('.mtop-btn[data-action="fullscreen"]');
    if (fsBtn) {
      const onFullscreenChange = () => {
        const isFS = !!(document.fullscreenElement || document.webkitFullscreenElement);
        fsBtn.classList.toggle('active', isFS);
      };
      document.addEventListener('fullscreenchange', onFullscreenChange, { signal: _ac.signal });
      document.addEventListener('webkitfullscreenchange', onFullscreenChange, { signal: _ac.signal });
    }
  }

  // ─── NAV DELEGATION ───
  if (nav) {
    log.debug('[mobileTopbar] #mobile-nav delegation active');
    nav.addEventListener('click', (e) => {
      const btn = e.target.closest('.mnav-btn');
      if (!btn) return;

      triggerHaptic();

      if (btn.dataset.nav && window.mobileNav) {
        try {
          window.mobileNav(btn.dataset.nav);
        } catch (e) {
          log.warn('[mobileTopbar] mobileNav error:', e.message);
        }
      }
    }, { signal: _ac.signal });
  }

  _initialized = true;
}

export function destroyMobileTopbar() {
  // Abort all listeners
  if (_ac) { _ac.abort(); _ac = null; }
  if (_autoHideAC) { _autoHideAC.abort(); _autoHideAC = null; }

  // [FIX] Cleanup timers и RAF
  clearTimeout(_autoHideTimer);
  _autoHideTimer = null;
  if (_autoHideRafId) {
    cancelAnimationFrame(_autoHideRafId);
    _autoHideRafId = null;
  }

  // [FIX] Reset inline styles установленные toggleCompactMode
  const { topbar, nav } = _getRefs();
  if (topbar) {
    topbar.style.gap = '';
    topbar.querySelectorAll('.mtop-btn').forEach(b => {
      b.style.padding = '';
      b.style.fontSize = '';
      b.style.display = '';
    });
    topbar.style.transform = '';
    topbar.style.opacity = '1';
  }
  if (nav) {
    nav.querySelectorAll('.mnav-btn').forEach(b => {
      b.style.padding = '';
      b.style.fontSize = '';
    });
  }

  // [FIX] Reset state
  _compactMode = false;
  _autoHideEnabled = false;
  _fullscreenPending = false;
  _initialized = false;
  _invalidateCache();
}

// ══════════════════════════════════════════════════════════════
//  ENTERPRISE FEATURES
// ══════════════════════════════════════════════════════════════

const TOPBAR_CONFIG_KEY = 'mikhmel_topbar_config';

export function hideTopbarButton(action) {
  const { topbar } = _getRefs();
  if (!topbar) return;
  topbar.querySelectorAll(`.mtop-btn[data-action="${action}"]`).forEach(b => b.style.display = 'none');
}

export function showTopbarButton(action) {
  const { topbar } = _getRefs();
  if (!topbar) return;
  topbar.querySelectorAll(`.mtop-btn[data-action="${action}"]`).forEach(b => b.style.display = '');
}

export function toggleCompactMode() {
  _compactMode = !_compactMode;
  const { topbar, nav } = _getRefs();

  if (topbar) {
    topbar.style.gap = _compactMode ? '1px' : '';
    topbar.querySelectorAll('.mtop-btn').forEach(b => {
      b.style.padding = _compactMode ? '2px 4px' : '';
      b.style.fontSize = _compactMode ? '10px' : '';
    });
  }
  if (nav) {
    nav.querySelectorAll('.mnav-btn').forEach(b => {
      b.style.padding = _compactMode ? '4px' : '';
      b.style.fontSize = _compactMode ? '9px' : '';
    });
  }
  return _compactMode;
}

export function enableAutoHide(delaySec = 5) {
  // [FIX] Cleanup previous auto-hide listeners
  if (_autoHideAC) _autoHideAC.abort();
  _autoHideAC = new AbortController();

  _autoHideEnabled = true;
  const { topbar } = _getRefs();
  if (!topbar) return;

  const resetTimer = () => _throttledAutoHideReset(delaySec);

  // [FIX] Используем отдельный AbortController
  document.addEventListener('touchstart', resetTimer, { passive: true, signal: _autoHideAC.signal });
  document.addEventListener('mousemove', resetTimer, { passive: true, signal: _autoHideAC.signal });
  resetTimer();
}

export function disableAutoHide() {
  _autoHideEnabled = false;

  // [FIX] Cleanup auto-hide listeners
  if (_autoHideAC) {
    _autoHideAC.abort();
    _autoHideAC = null;
  }

  clearTimeout(_autoHideTimer);
  _autoHideTimer = null;

  if (_autoHideRafId) {
    cancelAnimationFrame(_autoHideRafId);
    _autoHideRafId = null;
  }

  const { topbar } = _getRefs();
  if (topbar) {
    topbar.style.transform = '';
    topbar.style.opacity = '1';
  }
}

export function reorderTopbarButton(fromIndex, toIndex) {
  const { topbar } = _getRefs();
  if (!topbar) return;
  const btns = [...topbar.querySelectorAll('.mtop-btn')];
  if (fromIndex < 0 || fromIndex >= btns.length || toIndex < 0 || toIndex >= btns.length) return;
  const btn = btns[fromIndex];
  const ref = btns[toIndex];
  topbar.insertBefore(btn, fromIndex < toIndex ? ref.nextSibling : ref);
  _invalidateCache(); // DOM изменился
}

export function getMobileTopbarState() {
  const { topbar, nav } = _getRefs();
  return {
    initialized: _initialized,
    topbarButtons: topbar ? topbar.querySelectorAll('.mtop-btn').length : 0,
    navButtons: nav ? nav.querySelectorAll('.mnav-btn').length : 0,
    compactMode: _compactMode,
    autoHide: _autoHideEnabled,
    isMobile: window.innerWidth <= 768,
    isFullscreen: !!(document.fullscreenElement || document.webkitFullscreenElement),
    fullscreenPending: _fullscreenPending,
  };
}

export function setMobileTopbarVisible(v) {
  const { topbar } = _getRefs();
  if (topbar) topbar.style.display = v ? '' : 'none';
}

export function setMobileNavVisible(v) {
  const { nav } = _getRefs();
  if (nav) nav.style.display = v ? '' : 'none';
}

export function simulateMobileAction(action, target) {
  const { topbar } = _getRefs();
  if (!topbar) return;
  const b = topbar.querySelector(`.mtop-btn[data-action="${action}"]${target ? `[data-target="${target}"]` : ''}`);
  if (b) b.click();
}

export function getMobileTopbarButtons() {
  const { topbar } = _getRefs();
  if (!topbar) return [];
  return [...topbar.querySelectorAll('.mtop-btn')].map(b => ({
    action: b.dataset.action || '',
    text: b.textContent.trim(),
    active: b.classList.contains('active'),
    visible: b.style.display !== 'none'
  }));
}

export function toggleFullscreenAPI() {
  toggleFullscreen();
}